/**
 * @description Manages Firebase database connectivity based on page visibility. When a page becomes
 * hidden, a dedicated worker schedules the database to go offline after a configured delay. A worker
 * is required because browsers throttle main-thread timers in background tabs, making worker-based
 * timers the only reliable way to fire after the full delay. When the page becomes visible again,
 * the database is brought back online immediately.
 */

import { addFlagListener } from '../../modules/FeatureFlags.js';

/** @typedef {import('firebase/compat/app').default.database.Database} FirebaseDatabase */

// Defines the pages where the sleep manager will be active
export const SLEEP_MODE_PAGES = ['/map/', '/street-view/', '/photos/'];

// Prevents the sleep manager from being initialized multiple times
/** @type {Boolean} */
let sleepManagerInitialized = false;

/** @type {FirebaseDatabase} */
let sleepManagerDatabase = null;

/** @type {Worker} */
let sleepWorker = null;

/**
 * Monitors the visibility state of the window document and manages the online/offline state of a Firebase database accordingly.
 * - When the document becomes hidden, it schedules the database to go offline after a specified delay.
 * - When the document becomes visible again, brings the database back online immediately.
 * - Only runs on pages whose pathname matches one of the segments in {@link SLEEP_MODE_PAGES}.
 *
 * @param {FirebaseDatabase} database - The Firebase database instance with `goOffline` and `goOnline` methods.
 */
export function initPageSleepManager(database) {
  // Prevent multiple initializations of the sleep manager
  if (sleepManagerInitialized) return;
  sleepManagerInitialized = true;

  // Ensure that the monitoring only runs on the specified pages
  if (!SLEEP_MODE_PAGES.some((p) => location.pathname.includes(p))) return;

  sleepManagerDatabase = database;

  // Initialize the page sleep worker
  sleepWorker = new Worker('/source/_resources/workers/page-sleep-worker/page-sleep-worker-router.js', {
    type: 'module',
    name: 'page-sleep-worker'
  });
  sleepWorker.postMessage({ type: 'init' });

  // Set up a listener for messages from the worker to handle sleep/wake commands
  sleepWorker.onmessage = ({ data }) => pageSleepWorkerMessageHandler(data);

  addFlagListener('enable_katapult_sleep_manager', (enabled) => {
    if (enabled) setupSleepManager();
    else destroySleepManager();
  });
}

/** Forwards the current page visibility state to the sleep worker. */
function onVisibilityChange() {
  sleepWorker.postMessage({ type: 'visibilityChanged', visibility: document.visibilityState });
}

/**
 * Attaches the visibility change listener and syncs the worker with the current page visibility state.
 */
function setupSleepManager() {
  document.addEventListener('visibilitychange', onVisibilityChange);
  sleepWorker.postMessage({ type: 'visibilityChanged', visibility: document.visibilityState });
}

/**
 * Detaches the visibility change listener and forces the worker to treat the page as visible
 */
function destroySleepManager() {
  sleepWorker.postMessage({ type: 'visibilityChanged', visibility: 'visible' });
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

/**
 * Prevents the page from being put to sleep while a long-running operation is in progress.
 * Multiple calls can be active simultaneously. Call {@link allowPageSleep} when the operation is complete.
 */
export function preventPageSleep() {
  if (!sleepWorker) return;
  sleepWorker.postMessage({ type: 'preventPageSleep' });
}

/**
 * Allows the page to sleep again after a long-running operation completes.
 * Must be paired with a call to {@link preventPageSleep}.
 */
export function allowPageSleep() {
  if (!sleepWorker) return;
  sleepWorker.postMessage({ type: 'allowPageSleep' });
}

/**
 * Handles incoming messages from the page sleep worker
 * @param {object} message - The message object sent by the worker, expected to have a `type` property indicating the action to take (e.g., 'sleep' or 'wake').
 */
function pageSleepWorkerMessageHandler(message) {
  if (message.type == 'sleep') {
    sleepManagerDatabase.goOffline();
  } else if (message.type == 'wake') {
    sleepManagerDatabase.goOnline();
  }
}
